import {useEffect, useReducer} from 'react';
import type {IntlShape} from 'react-intl';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';

export const RUN_JUST_NOW_MINUTES = 5;
const MILLISECONDS_PER_MINUTE = 60 * 1000;
const RUN_DATE_FORMAT: Intl.DateTimeFormatOptions = {
	day: 'numeric',
	month: 'short',
};
const RUN_DATE_WITH_YEAR_FORMAT: Intl.DateTimeFormatOptions = {
	...RUN_DATE_FORMAT,
	year: 'numeric',
};

// Message ids for the two wordings of a run time. `at` takes {time}.
export type RunTimeMessages = {
	justNow: string;
	at: string;
};

const isSameDay = (date: Date, now: Date): boolean => date.toDateString() === now.toDateString();

// Values are the words' own placeholders besides {time}, given to both wordings.
export const formatRunTime = (
	finishedAt: string | null,
	now: Date,
	intl: IntlShape,
	messages: RunTimeMessages,
	values: Record<string, string> = {}
): string | null => {
	if (finishedAt === null) {
		return null;
	}
	const finishedDate = new Date(finishedAt);
	if (now.getTime() - finishedDate.getTime() < RUN_JUST_NOW_MINUTES * MILLISECONDS_PER_MINUTE) {
		return intl.formatMessage({id: messages.justNow}, values);
	}
	return intl.formatMessage(
		{id: messages.at},
		{
			...values,
			time: isSameDay(finishedDate, now) ? intl.formatTime(finishedDate) : intl.formatDate(finishedDate, RUN_DATE_FORMAT),
		}
	);
};

// When a run ended, as a lasting record: the time on the same day, else the date and time, with the year when it is not
// this year's. Never "just now".
export const formatPastRunTime = (finishedAt: string, now: Date, intl: IntlShape): string => {
	const finishedDate = new Date(finishedAt);
	const time = intl.formatTime(finishedDate);
	if (isSameDay(finishedDate, now)) {
		return intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_TODAY_AT}, {time});
	}
	return intl.formatMessage(
		{id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_DATE_AT},
		{
			date: intl.formatDate(
				finishedDate,
				finishedDate.getFullYear() === now.getFullYear() ? RUN_DATE_FORMAT : RUN_DATE_WITH_YEAR_FORMAT
			),
			time,
		}
	);
};

// Nothing in a store changes as a run grows older, so the caller is rendered again every minute while active. The tick
// count it answers lets a caller memoize what it works out from the time.
export const useMinuteTick = (isActive: boolean): number => {
	const [minuteTicks, showTimePassing] = useReducer((ticks: number) => ticks + 1, 0);
	useEffect(() => {
		if (!isActive) {
			return undefined;
		}
		const interval = setInterval(showTimePassing, MILLISECONDS_PER_MINUTE);
		return () => clearInterval(interval);
	}, [isActive]);
	return minuteTicks;
};
